import React, { useState, useEffect, useCallback, useMemo } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import api from '../services/api';
import {
  BarChart3, PlayCircle, Download, Info, TrendingUp, TrendingDown,
  Eye, ShoppingCart, DollarSign, Percent, ArrowUpRight, ArrowDownRight
} from 'lucide-react';

const periodOptions = [
  { value: 7, label: '7 días' },
  { value: 15, label: '15 días' },
  { value: 30, label: '30 días' },
];

function RangeControls({ value, from, to, onValueChange, onFromChange, onToChange }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {periodOptions.map(p => (
        <button key={p.value} onClick={() => onValueChange(p.value)}
          className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${value === p.value ? 'bg-blue-600 text-white' : 'bg-slate-800/50 text-slate-400 hover:text-white'}`}>
          {p.label}
        </button>
      ))}
      <button onClick={() => onValueChange('custom')}
        className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${value === 'custom' ? 'bg-blue-600 text-white' : 'bg-slate-800/50 text-slate-400 hover:text-white'}`}>
        Personalizado
      </button>
      {value === 'custom' && <>
        <label className="text-xs text-slate-400">Desde <input type="date" value={from} onChange={e => onFromChange(e.target.value)} className="ml-1 rounded bg-slate-900 border border-slate-700 px-2 py-1 text-xs text-white" /></label>
        <label className="text-xs text-slate-400">Hasta <input type="date" value={to} onChange={e => onToChange(e.target.value)} className="ml-1 rounded bg-slate-900 border border-slate-700 px-2 py-1 text-xs text-white" /></label>
      </>}
    </div>
  );
}

function rangeDates(value, from, to) {
  const isCustomComplete = value === 'custom' && from && to;
  const end = isCustomComplete ? new Date(`${to}T23:59:59.999`) : new Date();
  const dayCount = typeof value === 'number' ? value : 7;
  const start = isCustomComplete ? new Date(`${from}T00:00:00`) : new Date(end.getTime() - dayCount * 86400000);
  return { start, end };
}

function AnalysisGeneralTab() {
  const [days, setDays] = useState(7);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { start: from, end: now } = rangeDates(days, customFrom, customTo);
      if (days === 'custom' && (!customFrom || !customTo)) return;
      // The previous window ends one millisecond before the current one
      // starts. Ending it exactly at `from` put the boundary day in both
      // periods, inflating the "previous" side and skewing every comparison.
      const duration = now.getTime() - from.getTime();
      const prevTo = new Date(from.getTime() - 1);
      const prevFrom = new Date(prevTo.getTime() - duration);
      const res = await api.get(`/analytics/causality?date_from=${from.toISOString()}&date_to=${now.toISOString()}&prev_from=${prevFrom.toISOString()}&prev_to=${prevTo.toISOString()}`);
      setData(res.data);
    } catch (err) {
      console.error('Error loading analytics:', err);
      setData(null);
      setError('No se pudo cargar el análisis. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  }, [days, customFrom, customTo]);

  useEffect(() => { loadData(); }, [loadData]);

  const formatCurrency = (v) => {
    const sign = v >= 0 ? '' : '-';
    return sign + '$' + Math.abs(v).toLocaleString('es-MX', { minimumFractionDigits: 0 });
  };

  const formatPct = (v) => {
    const sign = v >= 0 ? '+' : '';
    return sign + v.toFixed(1) + '%';
  };

  if (loading) {
    return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" /></div>;
  }

  if (error) return <div role="alert" className="p-8 text-center text-red-400">
    <p>{error}</p><button onClick={loadData} className="mt-3 underline">Reintentar</button>
  </div>;

  const curr = data?.current || {};
  const prev = data?.previous || {};
  const changes = data?.changes || {};
  const impact = data?.impact || [];

  const { start: from, end: now } = rangeDates(days, customFrom, customTo);
  const prevFrom = new Date(from.getTime() - (now.getTime() - from.getTime()));
  const fmtDate = (d) => `${d.getDate()}/${String(d.getMonth()+1).padStart(2,'0')}`;

  return (
    <div className="space-y-5">
      {/* Info banner */}
      <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-3 flex items-center gap-2 text-xs text-slate-400">
        <Info size={14} className="text-blue-400 shrink-0" />
        <p><strong className="text-slate-300">Métricas Brutas</strong> (igual que Mercado Libre). Incluye órdenes pagadas sin reembolsos, según la última sincronización. Para ver ingresos netos, usa el Dashboard principal.</p>
      </div>

      {/* Period selector + Date range */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <RangeControls value={days} from={customFrom} to={customTo}
            onValueChange={setDays} onFromChange={setCustomFrom} onToChange={setCustomTo} />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-500">
            Periodo evaluado: {fmtDate(from)} - {fmtDate(now)}/{now.getFullYear()} vs {fmtDate(prevFrom)} - {fmtDate(from)}/{from.getFullYear()}
          </span>
          <button onClick={() => downloadChanges(days, customFrom, customTo)} className="flex items-center gap-2 px-3 py-1.5 border border-slate-700 rounded-lg text-xs text-slate-400 hover:text-white transition-colors">
            <Download size={14} />
            Descargar CSV
          </button>
        </div>
      </div>

      {/* AI Insight */}
      {data?.insight && (
        <div className="bg-slate-900/50 border border-blue-500/20 rounded-xl p-5">
          <p className="text-base text-white">{data.insight}</p>
          <div className="flex items-center justify-between mt-3">
            <span className="text-xs text-slate-500">{fmtDate(from)} - {fmtDate(now)}/{now.getFullYear()} vs {fmtDate(prevFrom)} - {fmtDate(from)}/{from.getFullYear()}</span>
            <button onClick={() => downloadOrders(days)} className="text-xs text-blue-400 hover:text-blue-300">Descargar órdenes ({days}d)</button>
          </div>
        </div>
      )}

      {/* Metric cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: 'Ingresos Brutos', current: formatCurrency(curr.revenue || 0), previous: formatCurrency(prev.revenue || 0), change: changes.revenue?.pct || 0 },
          { label: 'Cantidad de Ventas', current: (curr.units || 0).toLocaleString(), previous: (prev.units || 0).toLocaleString(), change: changes.units?.pct || 0 },
          { label: 'Visitas', current: (curr.visits || 0).toLocaleString(), previous: (prev.visits || 0).toLocaleString(), change: changes.visits?.pct || 0 },
          { label: 'Conversión', current: (curr.conversion || 0).toFixed(2) + '%', previous: (prev.conversion || 0).toFixed(2) + '%', change: changes.conversion?.pct || 0 },
        ].map(item => (
          <div key={item.label} className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-4">
            <p className="text-xs text-slate-400 mb-2">{item.label}</p>
            <p className="text-2xl font-bold text-white">{item.current}</p>
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-slate-500">ant: {item.previous}</span>
              <span className={`text-sm font-semibold ${item.change >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {formatPct(item.change)}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Impact by variable */}
      <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-5">
        <h3 className="text-base font-semibold text-white mb-4">Impacto en ventas por variable</h3>
        <div className="space-y-4">
          {impact.map(item => {
            const isPositive = item.impact >= 0;
            const maxAbsImpact = Math.max(...impact.map(i => Math.abs(i.impact)), 1);
            const barWidth = Math.min(Math.abs(item.impact) / maxAbsImpact * 100, 100);
            return (
              <div key={item.variable}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-white">{item.variable}</span>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-semibold ${isPositive ? 'text-emerald-400' : 'text-red-400'}`}>
                      {isPositive ? '+' : ''}{formatCurrency(item.impact)}
                    </span>
                    <span className="text-xs text-slate-500">({formatPct(item.pct)})</span>
                  </div>
                </div>
                <div className="w-full bg-slate-800 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${isPositive ? 'bg-emerald-500' : 'bg-red-500'}`}
                    style={{ width: `${barWidth}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Which listings moved the total. The period figures say revenue
          changed; this says what to actually look at. */}
      {(data?.mayor_caida?.length > 0 || data?.mayor_crecimiento?.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <MoversCard
            title="Mayor caída"
            items={data.mayor_caida || []}
            tone="down"
            formatCurrency={formatCurrency}
          />
          <MoversCard
            title="Mayor crecimiento"
            items={data.mayor_crecimiento || []}
            tone="up"
            formatCurrency={formatCurrency}
          />
        </div>
      )}
    </div>
  );
}

function MoversCard({ title, items, tone, formatCurrency }) {
  const isUp = tone === 'up';
  return (
    <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-5">
      <h3 className={`text-base font-semibold mb-4 ${isUp ? 'text-emerald-400' : 'text-red-400'}`}>
        {title}
      </h3>
      {items.length === 0 ? (
        <p className="text-sm text-slate-500">Sin movimientos en este periodo.</p>
      ) : (
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {items.map(item => (
            <div key={item.item_id} className="flex items-center gap-3">
              {item.thumbnail ? (
                <img src={item.thumbnail} alt="" className="w-10 h-10 rounded object-cover shrink-0" />
              ) : (
                <div className="w-10 h-10 rounded bg-slate-800 shrink-0" />
              )}
              <div className="min-w-0 flex-1">
                <p className="text-sm text-white truncate">{item.title}</p>
                <p className="text-xs text-slate-500">
                  {item.previous_units} → {item.units} uds
                  {item.motivo_unavailable_reason ? (
                    <span className="text-slate-500"> · {item.motivo_unavailable_reason}</span>
                  ) : item.motivo ? ` · ${item.motivo}` : ''}
                </p>
                <p className="text-xs text-slate-600">
                  {item.visits_change_pct == null ? '' : `Visitas ${item.visits_change_pct >= 0 ? '+' : ''}${item.visits_change_pct.toFixed(1)}%`}
                  {item.visits_change_pct != null && item.conversion_change_pct != null ? ' · ' : ''}
                  {item.conversion_change_pct == null ? '' : `Conv. ${item.conversion_change_pct >= 0 ? '+' : ''}${item.conversion_change_pct.toFixed(1)}%`}
                </p>
              </div>
              <span className={`text-sm font-semibold shrink-0 ${isUp ? 'text-emerald-400' : 'text-red-400'}`}>
                {isUp ? '+' : ''}{formatCurrency(item.change)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

async function downloadOrders(days) {
  try {
    const response = await api.get(`/dashboard/export-orders?period=${days}`, { responseType: 'blob' });
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ordenes_${days}d.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading orders:', error);
  }
}

async function downloadChanges(days, customFrom, customTo) {
  try {
    const { start, end } = rangeDates(days, customFrom, customTo);
    const params = { period: days, from: start.toISOString(), to: end.toISOString() };
    const response = await api.get('/analytics/changes/export', { params, responseType: 'blob' });
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'analytics_causalidad_30d.csv';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading changes:', error);
  }
}

function PublicationsTab() {
  const [days, setDays] = useState(7);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [report, setReport] = useState(null);
  const [connected, setConnected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [search, setSearch] = useState('');
  const [pareto, setPareto] = useState('');
  const [sort, setSort] = useState({ key: 'Revenue_30d', direction: -1 });
  const tableColumns = [
    ['Pareto', 'Pareto'], ['Titulo', 'Publicación'], ['Revenue_30d', 'Ingresos'],
    ['Revenue_Pct', '%'], ['Visitas_30d', 'Visitas'], ['Ratio_Ads_Pct', 'Tráfico (%Ads)'],
    ['Ventas_30d', 'Ventas'], ['Conversion_Pct', 'CVR'], ['CTR_Pct', 'CTR'],
    ['Stock', 'Stock'], ['Venta_Perdida', 'Vta Perdida'], ['Accionables', 'Accionables'],
  ];
  const [page, setPage] = useState(1);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      setReport(null);
      setConnected(null);
      try {
        const status = await api.get('/meli/status');
        if (cancelled) return;
        if (typeof status.data.connected !== 'boolean') throw new Error('Estado de conexión inválido');
        setConnected(status.data.connected);
        if (!status.data.connected) return;
        const { start, end } = rangeDates(days, customFrom, customTo);
        if (days === 'custom' && (!customFrom || !customTo)) return;
        const response = await api.get('/analytics/publications', {
          params: days === 'custom'
            ? { account_id: status.data.account_id, date_from: start.toISOString(), date_to: end.toISOString() }
            : { account_id: status.data.account_id, period: days },
        });
        if (!cancelled) setReport(response.data);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || 'No se pudo cargar el reporte. Intenta de nuevo.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [attempt, days, customFrom, customTo]);

  const rows = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('es');
    return (report?.rows || []).filter(row =>
      (!pareto || row.Pareto === pareto) &&
      ['Item_ID', 'SKU', 'Titulo'].some(key => String(row[key] ?? '').toLocaleLowerCase('es').includes(term))
    ).sort((a, b) => {
      const left = a[sort.key];
      const right = b[sort.key];
      if (left == null) return right == null ? 0 : 1;
      if (right == null) return -1;
      return sort.direction * (typeof left === 'number' && typeof right === 'number'
        ? left - right : String(left).localeCompare(String(right), 'es', { numeric: true }));
    });
  }, [report, search, pareto, sort]);

  const download = () => {
    const escape = value => {
      let text = String(value ?? '');
      // Prevent spreadsheet formulas in seller-controlled titles/SKUs.
      if (typeof value === 'string' && /^[=+@\-\t\r\n]/.test(text)) text = "'" + text;
      return '"' + text.replace(/"/g, '""') + '"';
    };
    const csv = [report.columns, ...rows.map(row => report.columns.map(key => row[key]))]
      .map(row => row.map(escape).join(',')).join('\r\n');
    const url = URL.createObjectURL(new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'analytics_publicaciones.csv';
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  if (loading) return <p role="status" className="p-8 text-center text-slate-400">Cargando publicaciones…</p>;
  if (error) return <div role="alert" className="p-8 text-center text-red-400">
    <p>{error}</p><button onClick={() => setAttempt(value => value + 1)} className="mt-3 underline">Reintentar</button>
  </div>;
  if (connected === false) return <p className="p-8 text-center text-slate-400">Conecta tu cuenta de Mercado Libre para ver el rendimiento detallado de cada publicación.</p>;

  const pages = Math.max(1, Math.ceil(rows.length / 50));
  const summary = report?.summary || {};
  return <div className="space-y-4">
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <RangeControls value={days} from={customFrom} to={customTo}
        onValueChange={setDays} onFromChange={setCustomFrom} onToChange={setCustomTo} />
      <p className="text-xs text-slate-500">Ingresos brutos: incluye órdenes generadas, incluso canceladas.</p>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
      {[['A', 'KILLER'], ['B', 'POTENCIAL'], ['C', 'COLA LARGA']].map(([group, label]) => <div key={group} className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-4"><p className="text-xs text-slate-400">{label}</p><p className="text-2xl font-bold text-white">{summary.pareto_counts?.[group] ?? 0}</p><p className="text-xs text-slate-500">publicaciones del reporte</p></div>)}
      <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-4"><p className="text-xs text-slate-400">Venta perdida</p><p className="text-2xl font-bold text-white">{summary.lost_revenue == null ? '—' : `$${summary.lost_revenue.toLocaleString('es-MX')}`}</p><p className="text-xs text-slate-500">Cobertura {summary.lost_revenue_coverage?.known ?? 0}/{summary.lost_revenue_coverage?.eligible ?? 0} publicaciones · {summary.stock_coverage?.observed_days ?? 0}/{summary.stock_coverage?.period_days ?? 0} días observados</p></div>
    </div>
    {report && <p className="text-xs text-slate-500">{new Date(report.date_from).toLocaleString('es-MX')} – {new Date(report.date_to).toLocaleString('es-MX')}</p>}
    <div className="flex flex-wrap gap-3">
      <input aria-label="Buscar publicaciones" placeholder="Buscar por título, SKU o Item ID" value={search}
        onChange={event => { setSearch(event.target.value); setPage(1); }} className="bg-slate-900 border border-slate-700 rounded-lg p-2 text-white flex-1" />
      <select aria-label="Filtrar Pareto" value={pareto} onChange={event => { setPareto(event.target.value); setPage(1); }} className="bg-slate-900 rounded-lg p-2 text-white">
        <option value="">Todos los Pareto</option>{['A', 'B', 'C'].map(value => <option key={value}>{value}</option>)}
      </select>
      <button onClick={download} disabled={!rows.length} className="flex items-center gap-2 rounded-lg border border-slate-700 p-2 text-white disabled:opacity-40"><Download size={16} />Descargar CSV</button>
    </div>
    <details className="text-xs text-slate-400">
      <summary className="cursor-pointer">Definiciones y datos no disponibles</summary>
        <p className="mt-2">Pareto: KILLER cubre el 80% de ingresos, POTENCIAL el 15% siguiente y COLA LARGA el 5% restante. Los ingresos son brutos y el rango se puede elegir arriba.</p>
      {Object.entries(report?.unavailable_columns || {}).map(([key, reason]) => <p key={key} className="mt-1">{key}: {reason}</p>)}
    </details>
    {!rows.length ? <p className="p-8 text-center text-slate-400">{report?.rows?.length ? 'Sin coincidencias para estos filtros.' : 'No hay publicaciones sincronizadas para esta cuenta.'}</p> : <>
      <div className="overflow-x-auto rounded-xl border border-slate-800">
        <table className="w-full text-sm text-slate-300">
          <caption className="sr-only">Rendimiento por publicación en el periodo seleccionado</caption>
          <thead className="bg-slate-900"><tr>{tableColumns.map(([key, label]) => <th scope="col" key={key} aria-sort={sort.key === key ? (sort.direction === 1 ? 'ascending' : 'descending') : 'none'} className="p-3 text-left whitespace-nowrap">
            <button onClick={() => { setSort({ key, direction: sort.key === key ? -sort.direction : 1 }); setPage(1); }}>{label}{sort.key === key ? (sort.direction === 1 ? ' ↑' : ' ↓') : ''}</button>
          </th>)}</tr></thead>
          <tbody>{rows.slice((page - 1) * 50, page * 50).map(row => <tr key={row.Item_ID} className="border-t border-slate-800">
            {tableColumns.map(([key]) => <td key={key} className={`p-3 ${key === 'Accionables' || key === 'Titulo' ? 'whitespace-normal min-w-[220px]' : 'whitespace-nowrap'}`} title={row[key] == null ? report.availability_by_item?.[row.Item_ID]?.[key] || report.unavailable_columns[key] || 'Dato no disponible' : undefined}>
              {key === 'Titulo' ? <><span className="text-white">{row.Titulo || '—'}</span><span className="block text-xs text-slate-500">{row.SKU || row.Item_ID}</span></> : row[key] == null ? '—' : typeof row[key] === 'number' ? row[key].toLocaleString('es-MX', { maximumFractionDigits: 4 }) : row[key]}
            </td>)}
          </tr>)}</tbody>
        </table>
      </div>
      <div className="flex items-center justify-between text-sm text-slate-400">
        <button disabled={page === 1} onClick={() => setPage(value => value - 1)} className="disabled:opacity-40">Anterior</button>
        <span>{rows.length} publicaciones · Página {page} de {pages}</span>
        <button disabled={page >= pages} onClick={() => setPage(value => value + 1)} className="disabled:opacity-40">Siguiente</button>
      </div>
    </>}
  </div>;
}

function ChangesTab() {
  const [days, setDays] = useState(7);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [changes, setChanges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { start, end } = rangeDates(days, customFrom, customTo);
        if (days === 'custom' && (!customFrom || !customTo)) return;
        const res = await api.get('/analytics/recent-changes', {
          params: { date_from: start.toISOString(), date_to: end.toISOString() },
        });
        setChanges(res.data.changes || []);
      } catch {
        setChanges([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [days, customFrom, customTo]);

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" /></div>;

  return (
    <div className="space-y-4">
      <RangeControls value={days} from={customFrom} to={customTo}
        onValueChange={setDays} onFromChange={setCustomFrom} onToChange={setCustomTo} />
      {changes.length === 0 ? (
        <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-8 text-center">
          <Info size={40} className="text-slate-600 mx-auto mb-3" />
          <h3 className="text-lg font-bold text-slate-400 mb-2">Sin cambios recientes</h3>
          <p className="text-sm text-slate-500">No se detectaron cambios significativos en las últimas 24 horas.</p>
        </div>
      ) : changes.map((change, i) => (
        <div key={i} className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-4 flex items-center justify-between">
          <div>
            <p className="text-sm text-white font-medium">{change.description}</p>
            <p className="text-xs text-slate-500 mt-1">{change.timestamp}</p>
          </div>
          <span className={`text-sm font-semibold ${change.impact >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            {change.impact >= 0 ? '+' : ''}{change.impact}%
          </span>
        </div>
      ))}
    </div>
  );
}

export default function AnalyticsPage() {
  const [activeTab, setActiveTab] = useState('general');

  const tabs = [
    { id: 'general', label: 'Análisis General' },
    { id: 'publications', label: 'Publicaciones' },
    { id: 'changes', label: 'Análisis de Cambios' },
  ];

  return (
    <DashboardLayout
      title="Analytics"
      icon={BarChart3}
      backTo="/dashboard"
      headerRight={
        <button className="flex items-center gap-2 px-3 py-2 border border-slate-700 rounded-lg text-sm text-slate-300 hover:bg-slate-800 transition-colors">
          <PlayCircle size={16} />
          Ver tutorial
        </button>
      }
    >
      {/* Tabs */}
      <div className="flex items-center gap-2 mb-6">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-blue-600 text-white'
                : 'bg-slate-800/50 text-slate-400 hover:text-white hover:bg-slate-800'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'general' && <AnalysisGeneralTab />}
      {activeTab === 'publications' && <PublicationsTab />}
      {activeTab === 'changes' && <ChangesTab />}
    </DashboardLayout>
  );
}
